import React, { useState, useRef } from 'react';
import styles from './Modal.module.css';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dashboard from '../Dashboard/Dashboard';
import { Textarea } from '@mantine/core';
import captionsStyles from './CaptionGenerationModal.module.css';


interface TitleGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProcessContent: (description?: string) => Promise<string>;
  mediaType: 'video' | 'audio' | 'none';
  mediaUrl?: string;
  isProcessing: boolean;
  showDashboard: boolean;
  jobId: string | null;
  onCompletionCallback: (projectId: string) => void;
  projectId?: string;
  existingTitleData?: any; // Add prop to check if titleData exists
}

const TitleGenerationModal: React.FC<TitleGenerationModalProps> = ({ 
  isOpen, 
  onClose, 
  onProcessContent,
  mediaType,
  mediaUrl,
  isProcessing,
  showDashboard,
  jobId,
  onCompletionCallback,
  projectId,
  existingTitleData
}) => {
  const { t } = useTranslation('titleDescriptionPage');
  const [selectedLanguage, setSelectedLanguage] = useState('auto');
    // Use try/catch to handle the case when useNavigate is not available
  let navigate: (path: string) => void;
  try {
    navigate = useNavigate();
  } catch (error) {
    // Create a dummy navigate function when Router context is not available
    navigate = (path: string) => {
      console.warn('Navigation attempted outside Router context:', path);
    };
  }

  // Project ID is used for rendering and handling process content
  
  const [description, setDescription] = useState('');
  const [mediaLoaded, setMediaLoaded] = useState(false);
  console.log("=================> existingTitleData", existingTitleData);


  // Language options - moved inside component to access t()
  const LANGUAGES = [
    { value: 'auto', label: `🌐 ${t('titleDescriptionPage.modal.languages.autoDetect')}` },
    { value: 'en', label: `🇺🇸 ${t('titleDescriptionPage.modal.languages.english')}` },
    { value: 'es', label: `🇪🇸 ${t('titleDescriptionPage.modal.languages.spanish')}` },
    { value: 'fr', label: `🇫🇷 ${t('titleDescriptionPage.modal.languages.french')}` },
    { value: 'de', label: `🇩🇪 ${t('titleDescriptionPage.modal..german')}` },
    { value: 'it', label: `🇮🇹 ${t('titleDescriptionPage.modal.languages.italian')}` },
    { value: 'pt', label: `🇵🇹 ${t('titleDescriptionPage.modal.languages.portuguese')}` },
    { value: 'ru', label: `🇷🇺 ${t('titleDescriptionPage.modal.languages.russian')}` },
    { value: 'ja', label: `🇯🇵 ${t('titleDescriptionPage.modal.languages.japanese')}` },
    { value: 'ko', label: `🇰🇷 ${t('titleDescriptionPage.modal.languages.korean')}` },
    { value: 'zh', label: `🇨🇳 ${t('titleDescriptionPage.modal.languages.chinese')}` },
    { value: 'ar', label: `🇸🇦 ${t('titleDescriptionPage.modal.languages.arabic')}` },
    { value: 'hi', label: `🇮🇳 ${t('titleDescriptionPage.modal.languages.hindi')}` },
    { value: 'nl', label: `🇳🇱 ${t('titleDescriptionPage.modal.languages.dutch')}` },
    { value: 'sv', label: `🇸🇪 ${t('titleDescriptionPage.modal.languages.swedish')}` },
    { value: 'no', label: `🇳🇴 ${t('titleDescriptionPage.modal.languages.norwegian')}` },
    { value: 'da', label: `🇩🇰 ${t('titleDescriptionPage.modal.languages.danish')}` },
    { value: 'fi', label: `🇫🇮 ${t('titleDescriptionPage.modal.languages.finnish')}` },
  ]; 
  
  // Hidden element to store project ID
  const hiddenProjectIdRef = (
    <div id="titleGenProjectId" data-project-id={projectId || ""} style={{ display: 'none' }}></div>
  );
  
  // Generate title and descriptions from the provided media and/or description
  const handleProcessContent = async () => {
    try {
      if (isProcessing) return; // Prevent multiple submissions
      
      console.log('Processing content with existing project ID:', projectId);
      // Construct instruction with language and description
      let instruction = '';
      if (selectedLanguage !== 'auto') {
        const languageLabel = LANGUAGES.find(lang => lang.value === selectedLanguage)?.label || '';
        instruction += `Generate captions in ${languageLabel.split(' ')[1] || selectedLanguage}. `;
      }

      // Call the provided callback to process the contenti
      const resultProjectId = await onProcessContent(description + instruction? `Important: +  ${instruction}` : '');
      
      // Store the project ID for the callback when processing is complete
      const projectIdRef = document.getElementById('titleGenProjectId');
      if (projectIdRef) {
        projectIdRef.setAttribute('data-project-id', resultProjectId);
      }
      
      // Proceed to title description page
      onClose();
      navigate(`/title-description/${resultProjectId}`);
    } catch (error) {
      console.error('Error processing content:', error);
    }
  };

  // Handler for using existing title data
  const handleUseExistingTitleData = () => {
    // Navigate to title-description page with existing project
    if (projectId) {
      console.log('Using existing title data for project:', projectId);
      navigate(`/title-description/${projectId}`);
      onClose();
    }
  };

  const renderMediaPreview = () => {
    if (!mediaUrl) return null;
    
    if (mediaType === 'video') {
      return (
        <div className={styles.videoPreview}>
          <video
            src={mediaUrl}
            controls
            onLoadedData={() => setMediaLoaded(true)}
            className={styles.video}
          >
            Your browser does not support HTML video.
          </video>
          {!mediaLoaded && <p style={{ color: '#ccc', fontSize: '12px' }}>Loading video preview...</p>}
        </div>
      );
    } else if (mediaType === 'audio') {
      return (
        <div className={styles.videoPreview}>
          <audio
            src={mediaUrl}
            controls
            onLoadedData={() => setMediaLoaded(true)}
            style={{ width: '100%', marginTop: '12px' }}
          >
            Your browser does not support HTML audio.
          </audio>
          {!mediaLoaded && <p style={{ color: '#ccc', fontSize: '12px' }}>Loading audio preview...</p>}
        </div>
      );
    }
    
    return null;
  };

  const getActionButtonText = () => {
    if (isProcessing) return t('titleDescriptionPage.loading.saving');
    
    if (mediaType === 'video') {
      return t('titleDescriptionPage.modal.buttons.confirm');
    } else if (mediaType === 'audio') {
      return t('titleDescriptionPage.modal.buttons.confirm');
    } else {
      return t('titleDescriptionPage.modal.buttons.confirm');
    }
  };

  return (
    <div className={`${styles.modalOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {hiddenProjectIdRef}
        <button className={styles.closeButton} onClick={onClose} aria-label="Close Modal">
          <X size={24} />
        </button>
        <h2 className={styles.modalTitle}>{t('titleDescriptionPage.modal.title')}</h2>
        
        <div className={styles.modalBody}>
          {/* Show media preview if available */}
          {renderMediaPreview()}
          
          {/* Show Dashboard during processing */}
          {showDashboard && jobId && (
            <div className={styles.dashboardContainer}>
              <Dashboard 
                jobId={jobId}
                callBackFunc={(_, projectId) => {
                  // Get the stored project ID
                  const storedProjectId = document.getElementById('titleGenProjectId')?.getAttribute('data-project-id');
                  
                  // Call the completion callback with the stored project ID
                  onCompletionCallback(projectId || storedProjectId || jobId);
                }}
                displayOption="compact"
              />
            </div>
          )}

          {/* Language Selector */}
          <div className={captionsStyles.inputGroup}>
            <label className={captionsStyles.label}>{t('titleDescriptionPage.modal.language')}</label>
            <select 
              className={captionsStyles.select}
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Description textarea */}
          <div style={{ marginTop: '16px' }}>
            <label htmlFor="description" className={styles.label}>
              {t('titleDescriptionPage.modal.uploadPrompt.label')}
            </label>
            <textarea
              id="description"
              placeholder={t('titleDescriptionPage.modal.uploadPrompt.placeholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isProcessing}
              style={{ 
                backgroundColor: '#333', 
                color: '#eee',
                border: '1px solid #555',
                borderRadius: '4px',
                padding: '8px',
                width: '96%',
              }}
            />
          </div>
          
          {/* Guidance text */}
          <p className={styles.prompt}>
            {t('titleDescriptionPage.modal.analysisInfo')}
            {projectId && (
              <span style={{ display: 'block', marginTop: '8px', color: '#4CAF50' }}>
                ✓ {t('titleDescriptionPage.modal.existingProjectNotice')}
              </span>
            )}
          </p>
        </div>
        
        <div className={styles.modalFooter}>
          <button
            className={styles.button}
            onClick={handleProcessContent}
            disabled={isProcessing}
          >
            {getActionButtonText()}
          </button>
          
          {/* Show "Use Existing" button if titleData exists */}
          {existingTitleData && Array.isArray(existingTitleData) && existingTitleData.length > 0 && (
            <button
              className={styles.buttonSecondary}
              onClick={handleUseExistingTitleData}
              disabled={isProcessing}
              style={{ backgroundColor: '#4CAF50', color: 'white' }}
            >
              Use Existing Title & Description
            </button>
          )}
          
          <button className={styles.buttonSecondary} onClick={onClose}>
            {t('titleDescriptionPage.modal.buttons.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TitleGenerationModal;